import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  QrCode,
  Printer,
  Download,
  RefreshCw,
  Save,
  AlertCircle,
  Ruler,
} from "lucide-react";
import { Product } from "@/types/stock";
import { useStock } from "@/context/StockContext";
import { generateProductEAN13Barcode } from "@/utils/barcodeUtils";
import BarcodeSticker from "./BarcodeSticker";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Paper size templates
interface PaperTemplate {
  id: string;
  name: string;
  width: number;
  height: number;
  description: string;
  recommendedTemplate: "compact" | "detailed";
}

const PAPER_TEMPLATES: PaperTemplate[] = [
  {
    id: "40x20",
    name: "40mm × 20mm",
    width: 40,
    height: 20,
    description: "Standard compact sticker",
    recommendedTemplate: "compact",
  },
  {
    id: "50x30",
    name: "50mm × 30mm",
    width: 50,
    height: 30,
    description: "Medium sticker with more details",
    recommendedTemplate: "detailed",
  },
  {
    id: "30x40",
    name: "30mm × 40mm",
    width: 30,
    height: 40,
    description: "Tall vertical sticker",
    recommendedTemplate: "compact",
  },
  {
    id: "60x40",
    name: "60mm × 40mm",
    width: 60,
    height: 40,
    description: "Large detailed sticker",
    recommendedTemplate: "detailed",
  },
  {
    id: "custom",
    name: "Custom Size",
    width: 40,
    height: 20,
    description: "Define your own dimensions",
    recommendedTemplate: "compact",
  },
];

interface BarcodeStickerDialogProps {
  product: Product;
  trigger?: React.ReactNode;
  onBarcodeGenerated?: (barcode: string) => void;
}

const BarcodeStickerDialog: React.FC<BarcodeStickerDialogProps> = ({
  product,
  trigger,
  onBarcodeGenerated,
}) => {
  const { t } = useTranslation();
  const { updateProduct } = useStock();
  const [open, setOpen] = useState(false);
  const [generatedBarcode, setGeneratedBarcode] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [stickerCount, setStickerCount] = useState(1);
  const [selectedPaperTemplate, setSelectedPaperTemplate] =
    useState<string>("40x20");
  const [stickerWidth, setStickerWidth] = useState(40);
  const [stickerHeight, setStickerHeight] = useState(20);
  const [stickerTemplate, setStickerTemplate] = useState<
    "compact" | "detailed"
  >("compact");
  const stickerRef = useRef<HTMLDivElement>(null);

  // Handle paper template change
  const handlePaperTemplateChange = (templateId: string) => {
    setSelectedPaperTemplate(templateId);
    const template = PAPER_TEMPLATES.find((t) => t.id === templateId);
    if (template) {
      setStickerWidth(template.width);
      setStickerHeight(template.height);
      setStickerTemplate(template.recommendedTemplate);
    }
  };

  // Print function using browser's native print functionality
  const handlePrint = () => {
    if (!stickerRef.current) {
      toast.error("Sticker content not ready for printing");
      return;
    }

    // Get all the styles from the current document
    const styles = Array.from(document.styleSheets)
      .map((styleSheet) => {
        try {
          return Array.from(styleSheet.cssRules)
            .map((rule) => rule.cssText)
            .join("\n");
        } catch (e) {
          // Silently handle cross-origin stylesheet issues
          return "";
        }
      })
      .join("\n");

    // Create a new window with just the sticker content
    const printWindow = window.open("", "_blank", "width=800,height=600");
    if (!printWindow) {
      toast.error("Please allow popups to enable printing");
      return;
    }

    // Get the full HTML including inline styles from the component
    const stickerHTML = stickerRef.current.outerHTML;

    // Get current paper template info
    const currentTemplate = PAPER_TEMPLATES.find(
      (t) => t.id === selectedPaperTemplate
    );
    const paperWidth = currentTemplate?.width || stickerWidth;
    const paperHeight = currentTemplate?.height || stickerHeight;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Barcode Sticker - ${product.name}</title>
          <meta charset="utf-8">
          <style>
            ${styles}

            /* Set exact paper size with zero margins */
            @page {
              size: ${paperWidth}mm ${paperHeight}mm;
              margin: 0;
            }

            * {
              box-sizing: border-box;
            }

            @media print {
              * {
                margin: 0;
                padding: 0;
              }

              html, body {
                width: ${paperWidth}mm;
                height: ${paperHeight}mm;
                margin: 0 !important;
                padding: 0 !important;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
                color-adjust: exact;
                overflow: hidden !important;
              }

              .no-print {
                display: none !important;
                height: 0 !important;
                margin: 0 !important;
                padding: 0 !important;
                visibility: hidden !important;
                position: absolute !important;
              }

              .print-container {
                width: ${paperWidth}mm !important;
                height: ${paperHeight}mm !important;
                margin: 0 !important;
                padding: 0 !important;
                border: none !important;
                box-sizing: border-box !important;
                page-break-inside: avoid !important;
                page-break-after: avoid !important;
                display: block !important;
                overflow: hidden !important;
              }

              .barcode-sticker-container {
                padding: 1.5mm 1mm 1mm 1mm !important;
                margin: 0 !important;
                border: none !important;
                border-radius: 0 !important;
                box-shadow: none !important;
              }
            }

            /* Screen preview styles */
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 20px;
              background: #f5f5f5;
            }

            .print-container {
              width: ${paperWidth}mm;
              height: ${paperHeight}mm;
              border: 1px solid #ccc;
              box-sizing: border-box;
              background: white;
              margin: 0 auto;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            }

            .print-button {
              margin: 20px auto;
              display: block;
              padding: 12px 24px;
              background: #007bff;
              color: white;
              border: none;
              border-radius: 6px;
              cursor: pointer;
              font-size: 16px;
              font-weight: 500;
              transition: background 0.2s;
            }

            .print-button:hover {
              background: #0056b3;
            }

            .print-info {
              text-align: center;
              margin: 10px auto 20px;
              padding: 12px;
              background: #e3f2fd;
              border: 1px solid #90caf9;
              border-radius: 6px;
              max-width: 400px;
              font-size: 14px;
            }

            .print-info strong {
              color: #1976d2;
            }
          </style>
        </head>
        <body>
          <div class="print-info no-print">
            <strong>Paper Size:</strong> ${paperWidth}mm × ${paperHeight}mm<br>
            <strong>Template:</strong> ${
              stickerTemplate === "compact" ? "Compact" : "Detailed"
            }<br>
            <strong>Copies:</strong> ${stickerCount}<br>
            <small style="color: #666; margin-top: 8px; display: block;">
              ⚠️ Ensure your printer is set to ${paperWidth}mm × ${paperHeight}mm paper size
            </small>
          </div>
          <button class="print-button no-print" onclick="window.print()">🖨️ Print Sticker</button>
          ${Array.from(
            { length: stickerCount },
            (_, i) => `
            <div class="print-container" ${
              i > 0 ? 'style="page-break-before: always;"' : ""
            }>
              ${stickerHTML}
            </div>
          `
          ).join("")}
          <script>
            // Auto-focus for better UX
            window.focus();
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();

    // Give the window time to load
    setTimeout(() => {
      printWindow.focus();
    }, 100);
  };

  const generateNewBarcode = async () => {
    try {
      setIsGenerating(true);
      const result = await generateProductEAN13Barcode(product.id);
      setGeneratedBarcode(result.barcode);
      toast.success(t("stock.barcodeGenerated"));
    } catch (error) {
      console.error("Error generating barcode:", error);
      toast.error(t("stock.failedToGenerateBarcode"));
    } finally {
      setIsGenerating(false);
    }
  };

  const saveBarcode = async () => {
    if (!generatedBarcode) {
      toast.error(t("stock.noBarcodeToSave"));
      return;
    }

    try {
      setIsSaving(true);
      await updateProduct(product.id, {
        name: product.name,
        description: product.description,
        sku: product.sku,
        barcode: generatedBarcode,
        categoryId: product.categoryId,
        price: product.price,
        cost: product.cost,
        stockQuantity: product.stockQuantity,
        minStockLevel: product.minStockLevel,
        maxStockLevel: product.maxStockLevel,
        isActive: product.isActive,
        imageUrl: product.imageUrl,
      });

      onBarcodeGenerated?.(generatedBarcode);
      toast.success(t("stock.barcodeSaved"));
      setOpen(false);
    } catch (error) {
      console.error("Error saving barcode:", error);
      toast.error(t("stock.failedToSaveBarcode"));
    } finally {
      setIsSaving(false);
    }
  };

  const downloadSticker = async () => {
    if (!stickerRef.current) return;

    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(stickerRef.current, {
        scale: 8,
        backgroundColor: "#ffffff",
        logging: false,
      });

      const link = document.createElement("a");
      link.download = `barcode-sticker-${product.sku}.png`;
      link.href = canvas.toDataURL("image/png", 1.0);
      link.click();
      toast.success(t("stock.stickerDownloaded"));
    } catch (error) {
      console.error("Error downloading sticker:", error);
      toast.error(t("stock.failedToDownloadSticker"));
    }
  };

  const defaultTrigger = (
    <Button variant="outline" size="sm" className="flex items-center gap-2">
      <QrCode className="h-4 w-4" />
      {t("stock.printSticker")}
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || defaultTrigger}</DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="h-5 w-5" />
            {t("stock.barcodeSticker")} - {product.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Product Info */}
          <Card>
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold">{product.name}</h3>
                  <p className="text-sm text-gray-600">{product.description}</p>
                  <p className="text-sm font-mono">SKU: {product.sku}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-green-600">
                    {product.price.toFixed(3)} TND
                  </p>
                  {product.barcode ? (
                    <Badge variant="secondary">
                      {t("stock.productHasBarcode")}
                    </Badge>
                  ) : (
                    <Badge variant="outline">
                      {t("stock.productNoBarcode")}
                    </Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Barcode Generation for products without barcodes */}
          {!product.barcode && !generatedBarcode && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                  <span className="text-sm">
                    {t("stock.generateNewBarcode")}
                  </span>
                </div>
                <Button
                  onClick={generateNewBarcode}
                  disabled={isGenerating}
                  className="w-full"
                >
                  {isGenerating ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <QrCode className="h-4 w-4 mr-2" />
                      {t("stock.generateEAN13Barcode")}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Sticker Preview - Show if product has barcode or barcode was generated */}
          {(product.barcode || generatedBarcode) && (
            <>
              <Card>
                <CardContent className="p-4">
                  <div className="space-y-4 mb-4">
                    <div className="flex items-center gap-2">
                      <Ruler className="h-5 w-5 text-blue-600" />
                      <h3 className="font-semibold">
                        {t("stock.stickerPreview")}
                      </h3>
                    </div>

                    {/* Paper Template Selector */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">
                        {t("stock.paperSizeTemplate")}
                      </Label>
                      <Select
                        value={selectedPaperTemplate}
                        onValueChange={handlePaperTemplateChange}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue
                            placeholder={t("stock.selectPaperSize")}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {PAPER_TEMPLATES.map((template) => (
                            <SelectItem key={template.id} value={template.id}>
                              <div className="flex flex-col">
                                <span className="font-medium">
                                  {template.name}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {template.description}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      {/* Custom Size Inputs - Only show when custom is selected */}
                      {selectedPaperTemplate === "custom" && (
                        <div>
                          <Label className="text-sm mb-2 block">
                            {t("stock.customSize")} (mm)
                          </Label>
                          <div className="flex gap-2">
                            <Input
                              type="number"
                              placeholder="Width"
                              value={stickerWidth}
                              onChange={(e) =>
                                setStickerWidth(Number(e.target.value))
                              }
                              className="w-20"
                              min="20"
                              max="100"
                            />
                            <span className="self-center">×</span>
                            <Input
                              type="number"
                              placeholder="Height"
                              value={stickerHeight}
                              onChange={(e) =>
                                setStickerHeight(Number(e.target.value))
                              }
                              className="w-20"
                              min="15"
                              max="100"
                            />
                          </div>
                        </div>
                      )}

                      {/* Current Size Display - Show when not custom */}
                      {selectedPaperTemplate !== "custom" && (
                        <div>
                          <Label className="text-sm mb-2 block">
                            {t("stock.paperSize")}
                          </Label>
                          <div className="flex items-center gap-2 p-2 bg-blue-50 rounded border border-blue-200">
                            <Ruler className="h-4 w-4 text-blue-600" />
                            <span className="text-sm font-medium text-blue-900">
                              {stickerWidth}mm × {stickerHeight}mm
                            </span>
                          </div>
                        </div>
                      )}

                      <div>
                        <Label className="text-sm mb-2 block">
                          {t("stock.layoutTemplate")}
                        </Label>
                        <div className="flex gap-2">
                          <Button
                            variant={
                              stickerTemplate === "compact"
                                ? "default"
                                : "outline"
                            }
                            size="sm"
                            onClick={() => setStickerTemplate("compact")}
                          >
                            Compact
                          </Button>
                          <Button
                            variant={
                              stickerTemplate === "detailed"
                                ? "default"
                                : "outline"
                            }
                            size="sm"
                            onClick={() => setStickerTemplate("detailed")}
                          >
                            Detailed
                          </Button>
                        </div>
                      </div>

                      <div>
                        <Label
                          htmlFor="stickerCount"
                          className="text-sm mb-2 block"
                        >
                          {t("stock.copies")}
                        </Label>
                        <Input
                          id="stickerCount"
                          type="number"
                          min="1"
                          max="50"
                          value={stickerCount}
                          onChange={(e) =>
                            setStickerCount(parseInt(e.target.value) || 1)
                          }
                          className="w-20"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-center">
                    <BarcodeSticker
                      ref={stickerRef}
                      product={product}
                      barcode={generatedBarcode || product.barcode}
                      width={stickerWidth}
                      height={stickerHeight}
                      template={stickerTemplate}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={handlePrint}
                  className="flex items-center gap-2"
                >
                  <Printer className="h-4 w-4" />
                  {t("stock.printSticker")}
                </Button>

                <Button
                  onClick={downloadSticker}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  {t("stock.downloadSticker")}
                </Button>

                {/* Save barcode button - only show if barcode was generated and not saved yet */}
                {generatedBarcode && !product.barcode && (
                  <Button
                    onClick={saveBarcode}
                    disabled={isSaving}
                    variant="secondary"
                    className="flex items-center gap-2"
                  >
                    {isSaving ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4" />
                        {t("stock.saveBarcodeToProduct")}
                      </>
                    )}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BarcodeStickerDialog;
